import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import Avatar from '@atlaskit/avatar';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import {
  ApprovalStatus as ApprovalStatusType,
  ApprovalType,
  EntityType,
  GroupMember,
  TaskDetail,
  UserRole,
  useApprovalDeskPermissions,
} from '@css-xp/apd-common';
import { FormattedDate, getGSACBaseURL } from '@css-xp/base';
import { ApprovalStatus, EntitlementPageLink, SlackChannelLink } from '../../../common';
import { DisplayRequestType } from '../../../constants/approvals';
import { OPEN_TASK_STATUS } from '../../../constants/task';
import { TaskAction } from '../../../task-actions/task-actions';
import { ChangeAssignee } from '../../../task-list/actions';
import TaskDetailGrid from './task-detail-grid';
import './task-detail-tab.scss';

interface RequestPageProps {
  taskDetail: TaskDetail;
  onSelectGroupMember?: (_param: GroupMember) => void;
  userRole?: UserRole | null;
  openModal?: (_param: TaskAction) => void;
  selectedGroupMember?: GroupMember;
}

export const TaskDetailTab: FC<RequestPageProps> = ({
  taskDetail,
  openModal,
  userRole,
  onSelectGroupMember,
  selectedGroupMember,
}) => {
  const { hasTaskManagerWriteAccess } = useApprovalDeskPermissions();

  const { processVariables, taskVariables, taskProperties } = taskDetail;
  const approvalType = processVariables?.requestType;
  const isApprovalTypeRefund =
    approvalType === ApprovalType.TAX_REFUND || approvalType === ApprovalType.SUBSCRIPTION_REFUND;
  const evaluatedEntityInformation = processVariables?.evaluatedEntityInformation;
  const isApprovalTypeQuote =
    approvalType === ApprovalType.QUOTE_DISCOUNT || approvalType === ApprovalType.QUOTE_EXTENSION;
  const groupId = processVariables?.approvalEntityResult?.approverGroup as string | undefined;
  const approver = taskDetail?.ownerName || processVariables?.approvalEntityResult?.approverGroup;
  const discountAmount = processVariables?.approvalEntityResult?.discountValue;
  const discountPercentage = processVariables?.approvalEntityResult?.discountPercentage;
  const toPricingPlanKey = processVariables?.approvalEntityResult?.toPricingPlanKey;
  const businessKey = taskDetail?.businessKey;
  const entityType = isApprovalTypeRefund
    ? evaluatedEntityInformation?.find((entity) => entity?.entityId === businessKey)?.entityType
    : taskDetail?.processVariables?.approvalEntityResult?.entityType;
  const slackMessageUrl = taskVariables?.slackMessageUrl;
  const approvalStatus = taskVariables?.approvalStatus;
  const isChangeAssigneeAllowed =
    hasTaskManagerWriteAccess &&
    userRole &&
    [UserRole.SUPERUSER].includes(userRole) &&
    OPEN_TASK_STATUS.includes(approvalStatus as ApprovalStatusType);
  const isDiscountAmountValid = discountAmount !== undefined && discountAmount !== null && discountAmount > 0;
  const isDiscountPercentageValid =
    discountPercentage !== undefined && discountPercentage !== null && discountPercentage > 0;
  const isDiscountApprovalRequired = taskDetail?.processVariables?.discountApprovalRequiredInPricingPlan;

  return (
    <div className="task-detail-tab">
      <div className="task-detail-grid">
        <TaskDetailGrid label="Status">{approvalStatus && <ApprovalStatus status={approvalStatus} />}</TaskDetailGrid>
        <TaskDetailGrid
          label={isApprovalTypeRefund ? 'Invoice number' : isApprovalTypeQuote ? 'Quote number' : 'Entitlement number'}
        >
          {businessKey && entityType && (
            <EntitlementPageLink
              entityType={entityType}
              entityNumber={businessKey}
              entityId={processVariables?.approvalEntityResult?.entityId}
            />
          )}
        </TaskDetailGrid>
        <TaskDetailGrid label="Request type" testId="approval-type">
          {isApprovalTypeRefund && (
            <>
              {processVariables?.approvalEntityResult?.refundType}
              {evaluatedEntityInformation && evaluatedEntityInformation?.length > 1 && (
                <div className="standard-limit">
                  Refund requested for:
                  {evaluatedEntityInformation.map((item, index) => {
                    if (item?.entityType && item?.entityId && item?.entityType === EntityType.ENTITLEMENT) {
                      return (
                        <p key={index}>
                          <EntitlementPageLink entityType={item.entityType} entityNumber={item.entityId} />
                        </p>
                      );
                    }
                  })}
                </div>
              )}
            </>
          )}
          {approvalType === ApprovalType.QUOTE_EXTENSION && (
            <>
              Extend quote for {processVariables?.approvalEntityResult?.quoteExtensionDaysRequested} days
              <div className="standard-limit">
                Total days extended so far: {processVariables?.approvalEntityResult?.currentTotalQuoteExtensionLength}
              </div>
            </>
          )}
          {approvalType === ApprovalType.TRIAL_EXTENSION && (
            <>
              Extend trial for {processVariables?.approvalEntityResult?.trialExtensionDaysRequested} days
              <div className="standard-limit">
                Total days on trial so far: {processVariables?.approvalEntityResult?.currentTotalTrialLength}
              </div>
            </>
          )}
          {approvalType === ApprovalType.QUOTE_DISCOUNT && (
            <span data-testid="quote-discount-amount">
              {discountAmount !== undefined && discountAmount !== null && (
                <>USD {discountAmount.toLocaleString()} quote discount</>
              )}
            </span>
          )}
          {approvalType === ApprovalType.PRICING_PLAN_CHANGE && (
            <span data-testid="pricing-plan">
              Pricing plan change to {toPricingPlanKey}
              {(isDiscountAmountValid || isDiscountPercentageValid) && isDiscountApprovalRequired && <> | </>}
              {isDiscountPercentageValid && isDiscountApprovalRequired && <>{discountPercentage}% discount</>}
              {isDiscountAmountValid && isDiscountApprovalRequired && (
                <> / USD {discountAmount!.toLocaleString()} discount</>
              )}
            </span>
          )}
          {approvalType === ApprovalType.ORDER_DISCOUNT && (
            <span data-testid="discount-amount">
              {discountPercentage}% discount
              {discountAmount !== undefined && discountAmount !== null && (
                <> / USD {discountAmount.toLocaleString()} discount</>
              )}
            </span>
          )}
          {approvalType === ApprovalType.PRODUCT_DELETION && DisplayRequestType.PRODUCT_DELETION}
        </TaskDetailGrid>
        {approvalType === ApprovalType.QUOTE_EXTENSION && (
          <TaskDetailGrid label="Quote created" testId="quote-created">
            {taskProperties?.created && <FormattedDate date={taskProperties?.created} format={NEW_DATE_FORMAT} />}
          </TaskDetailGrid>
        )}
        <TaskDetailGrid label="Initiator">
          {processVariables?.initiatorName && (
            <div className="user-wrapper">
              <Avatar src={undefined} size="small" />
              <p className="user">{processVariables?.initiatorName}</p>
            </div>
          )}
        </TaskDetailGrid>
        <TaskDetailGrid label="Approver" testId="approver">
          {isChangeAssigneeAllowed ? (
            <ChangeAssignee
              groupId={groupId}
              requestType={approvalType as string}
              onSelectGroupMember={onSelectGroupMember}
              openModal={openModal}
              showSelectDropdown={true}
              defaultSelectedMember={{ name: approver as string, userId: taskDetail?.taskProperties?.owner }}
              selectedGroupMember={selectedGroupMember}
            />
          ) : (
            approver && (
              <div className="user-wrapper">
                <Avatar src={undefined} size="small" />
                <p className="user">{approver}</p>
              </div>
            )
          )}
        </TaskDetailGrid>
        <TaskDetailGrid label="Support ticket">
          {processVariables?.gsacId && (
            <Link target="_blank" to={{ pathname: `${getGSACBaseURL()}/browse/${processVariables.gsacId}` }}>
              {processVariables.gsacId}
            </Link>
          )}
        </TaskDetailGrid>
        <TaskDetailGrid label="Slack thread">
          <SlackChannelLink url={slackMessageUrl}>
            {processVariables?.approvalEntityResult?.slackChannel}
          </SlackChannelLink>
        </TaskDetailGrid>
      </div>
    </div>
  );
};
